/**
 * Program Category Edit Modal
 *
 * Modal for creating and editing program categories
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { drugProgramAPI, apiHelpers } from '../services/api'
import useDraftStore from '../stores/draftStore'
import { useAuthStore } from '../stores/authStore'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Checkbox,
  Textarea,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from './ui'

const DEFAULT_REQUIRED_METRICS = JSON.stringify([
  { metric_name: 'bmi', min: 18, max: 40 }
], null, 2)

const DEFAULT_MARKETING_COPY = JSON.stringify({
  intake: {
    title: 'Program Intake',
    description: 'Welcome to the program'
  }
}, null, 2)

export default function ProgramCategoryEditModal({ category, isOpen, onClose, onSave }) {
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const [formData, setFormData] = useState({
    category_key: '',
    display_name: '',
    allow_multiple_concurrent: false,
    program_definition_ids: [],
    required_metrics: [],
    marketing_copy: {}
  })
  const [validationErrors, setValidationErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // For linked programs selection
  const [availablePrograms, setAvailablePrograms] = useState([])
  const [loadingPrograms, setLoadingPrograms] = useState(false)
  const [programSearch, setProgramSearch] = useState('')

  // JSON editor state
  const [requiredMetricsJson, setRequiredMetricsJson] = useState('[]')
  const [marketingCopyJson, setMarketingCopyJson] = useState('{}')
  const [jsonErrors, setJsonErrors] = useState({})

  const { currentClinicId } = useAuthStore()
  const clinicId = currentClinicId || searchParams.get('clinic') || 'tmates'

  // Initialize draft store when modal opens
  useEffect(() => {
    if (isOpen && user && clinicId) {
      const { initialize, isInitialized, userId: storeUserId } = useDraftStore.getState()
      const userId = user.id

      if (!isInitialized || storeUserId !== userId) {
        initialize(clinicId, userId)
      }
    }
  }, [isOpen, user, clinicId])

  // Initialize form data when category changes
  useEffect(() => {
    if (category) {
      setFormData({
        _id: category._id,
        category_key: category.category_key || '',
        display_name: category.display_name || '',
        allow_multiple_concurrent: category.allow_multiple_concurrent ?? false,
        program_definition_ids: category.program_definition_ids || [],
        required_metrics: category.required_metrics || [],
        marketing_copy: category.marketing_copy || {}
      })
      setRequiredMetricsJson(
        category.required_metrics?.length > 0
          ? JSON.stringify(category.required_metrics, null, 2)
          : '[]'
      )
      setMarketingCopyJson(
        Object.keys(category.marketing_copy || {}).length > 0
          ? JSON.stringify(category.marketing_copy, null, 2)
          : '{}'
      )
      setHasChanges(false)
    } else {
      // New category
      setFormData({
        category_key: '',
        display_name: '',
        allow_multiple_concurrent: false,
        program_definition_ids: [],
        required_metrics: [],
        marketing_copy: {}
      })
      setRequiredMetricsJson('[]')
      setMarketingCopyJson('{}')
      setHasChanges(false)
    }
    setValidationErrors({})
    setJsonErrors({})
  }, [category, isOpen])

  // Fetch available programs when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailablePrograms()
    }
  }, [isOpen])

  const fetchAvailablePrograms = async () => {
    setLoadingPrograms(true)
    try {
      const response = await drugProgramAPI.list({ limit: 1000 })
      if (response.data.success) {
        setAvailablePrograms(response.data.programs || response.data.data || [])
      }
    } catch (error) {
      console.error('Failed to fetch programs:', error)
      setAvailablePrograms([])
    } finally {
      setLoadingPrograms(false)
    }
  }

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setHasChanges(true)
  }

  // Program selection helpers
  const getSelectedPrograms = () => {
    const selectedIds = formData.program_definition_ids || []
    return availablePrograms.filter(p =>
      selectedIds.includes(p._id) || selectedIds.includes(p.entity_id)
    )
  }

  const getFilteredAvailablePrograms = () => {
    const selectedIds = new Set(formData.program_definition_ids || [])
    return availablePrograms.filter(p => {
      // Exclude already selected
      if (selectedIds.has(p._id) || selectedIds.has(p.entity_id)) return false

      // Apply search filter
      if (programSearch) {
        const search = programSearch.toLowerCase()
        return (
          p.drug_name?.toLowerCase().includes(search) ||
          p.entity_id?.toLowerCase().includes(search)
        )
      }
      return true
    })
  }

  const addProgram = (program) => {
    const newIds = [...(formData.program_definition_ids || []), program._id]
    updateFormData('program_definition_ids', newIds)
  }

  const removeProgram = (programId) => {
    const newIds = (formData.program_definition_ids || []).filter(id => id !== programId)
    updateFormData('program_definition_ids', newIds)
  }

  // JSON validation
  const validateJson = (jsonString, fieldName) => {
    try {
      JSON.parse(jsonString)
      return { valid: true }
    } catch (e) {
      return { valid: false, error: `Invalid JSON: ${e.message}` }
    }
  }

  const handleRequiredMetricsChange = (value) => {
    setRequiredMetricsJson(value)
    setHasChanges(true)

    const validation = validateJson(value, 'required_metrics')
    if (validation.valid) {
      setJsonErrors(prev => ({ ...prev, required_metrics: null }))
      try {
        const parsed = JSON.parse(value)
        setFormData(prev => ({ ...prev, required_metrics: parsed }))
      } catch (e) {}
    } else {
      setJsonErrors(prev => ({ ...prev, required_metrics: validation.error }))
    }
  }

  const handleMarketingCopyChange = (value) => {
    setMarketingCopyJson(value)
    setHasChanges(true)

    const validation = validateJson(value, 'marketing_copy')
    if (validation.valid) {
      setJsonErrors(prev => ({ ...prev, marketing_copy: null }))
      try {
        const parsed = JSON.parse(value)
        setFormData(prev => ({ ...prev, marketing_copy: parsed }))
      } catch (e) {}
    } else {
      setJsonErrors(prev => ({ ...prev, marketing_copy: validation.error }))
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.category_key?.trim()) {
      errors.category_key = 'Category key is required'
    } else if (!/^[a-z0-9_]+$/.test(formData.category_key)) {
      errors.category_key = 'Category key must be lowercase letters, numbers, and underscores only'
    }

    if (!formData.display_name?.trim()) {
      errors.display_name = 'Display name is required'
    }

    // Validate JSON fields
    const metricsValidation = validateJson(requiredMetricsJson, 'required_metrics')
    if (!metricsValidation.valid) {
      errors.required_metrics = metricsValidation.error
    }

    const copyValidation = validateJson(marketingCopyJson, 'marketing_copy')
    if (!copyValidation.valid) {
      errors.marketing_copy = copyValidation.error
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setIsLoading(true)
    try {
      const { addChange } = useDraftStore.getState()

      // Parse JSON fields one more time to ensure they're current
      let finalFormData = { ...formData }
      try {
        finalFormData.required_metrics = JSON.parse(requiredMetricsJson)
        finalFormData.marketing_copy = JSON.parse(marketingCopyJson)
      } catch (e) {
        console.error('JSON parse error on save:', e)
      }

      const isNewCategory = !category || !category._id
      const operation = isNewCategory ? 'create' : 'update'

      const details = []
      if (finalFormData.program_definition_ids?.length) {
        details.push(`${finalFormData.program_definition_ids.length} programs`)
      }
      if (finalFormData.allow_multiple_concurrent) {
        details.push('multi-concurrent')
      }

      const description = operation === 'create'
        ? `Create category: ${finalFormData.display_name}${details.length ? ` (${details.join(', ')})` : ''}`
        : `Update category: ${finalFormData.display_name}${details.length ? ` (${details.join(', ')})` : ''}`

      addChange({
        resource_type: 'program_category',
        resource_id: category?.category_key || finalFormData.category_key,
        operation: operation,
        original_data: category || null,
        new_data: finalFormData,
        description: description
      })

      console.log(`[DRAFT] Saved ${operation} for program category:`, finalFormData.display_name)

      onSave?.(finalFormData)
      onClose()
      setHasChanges(false)
    } catch (error) {
      console.error('Failed to save program category to drafts:', error)
      alert('Failed to save changes. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose()
        setHasChanges(false)
      }
    } else {
      onClose()
    }
  }

  const isEditing = category && category._id

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? `Edit Category - ${category.display_name}` : 'Create New Program Category'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update program category information, linked programs, and configuration.'
              : 'Create a new program category to group drug programs together.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="programs">Linked Programs</TabsTrigger>
            <TabsTrigger value="metrics">Required Metrics</TabsTrigger>
            <TabsTrigger value="marketing">Marketing Copy</TabsTrigger>
          </TabsList>

          {/* Basic Information Tab */}
          <TabsContent value="basic" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category_key">Category Key *</Label>
                <Input
                  id="category_key"
                  value={formData.category_key || ''}
                  onChange={(e) => updateFormData('category_key', e.target.value.toLowerCase())}
                  placeholder="e.g., weight_loss_programs"
                  disabled={isEditing}
                  className={validationErrors.category_key ? 'border-red-500' : ''}
                />
                {validationErrors.category_key && (
                  <p className="text-sm text-red-500">{validationErrors.category_key}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Unique identifier (lowercase, underscores allowed)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name *</Label>
                <Input
                  id="display_name"
                  value={formData.display_name || ''}
                  onChange={(e) => updateFormData('display_name', e.target.value)}
                  placeholder="e.g., Weight Loss Programs"
                  className={validationErrors.display_name ? 'border-red-500' : ''}
                />
                {validationErrors.display_name && (
                  <p className="text-sm text-red-500">{validationErrors.display_name}</p>
                )}
              </div>
            </div>

            <div className="space-y-4 pt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allow_multiple_concurrent"
                  checked={formData.allow_multiple_concurrent || false}
                  onCheckedChange={(checked) => updateFormData('allow_multiple_concurrent', checked)}
                />
                <Label htmlFor="allow_multiple_concurrent" className="cursor-pointer">
                  Allow Multiple Concurrent Enrollments
                </Label>
              </div>
              <p className="text-xs text-muted-foreground ml-6">
                When enabled, patients can be enrolled in multiple programs within this category simultaneously.
              </p>
            </div>
          </TabsContent>

          {/* Linked Programs Tab */}
          <TabsContent value="programs" className="space-y-4">
            <div>
              <Label className="text-base font-medium">Selected Programs ({formData.program_definition_ids?.length || 0})</Label>
              <div className="flex flex-wrap gap-2 mt-2 min-h-[40px] p-2 border rounded-md bg-muted/30">
                {getSelectedPrograms().length === 0 ? (
                  <span className="text-sm text-muted-foreground">No programs selected</span>
                ) : (
                  getSelectedPrograms().map(program => (
                    <Badge
                      key={program._id}
                      variant="secondary"
                      className="flex items-center gap-1 pr-1"
                    >
                      {program.drug_name}
                      <button
                        type="button"
                        onClick={() => removeProgram(program._id)}
                        className="ml-1 rounded-full hover:bg-muted p-0.5"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </Badge>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="program_search">Search Programs</Label>
              <Input
                id="program_search"
                value={programSearch}
                onChange={(e) => setProgramSearch(e.target.value)}
                placeholder="Search by drug name or entity ID..."
              />
            </div>

            <div className="border rounded-md max-h-[300px] overflow-y-auto">
              {loadingPrograms ? (
                <div className="p-4 text-center text-muted-foreground">Loading programs...</div>
              ) : getFilteredAvailablePrograms().length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  {programSearch ? 'No programs match your search' : 'All programs have been selected'}
                </div>
              ) : (
                <div className="divide-y">
                  {getFilteredAvailablePrograms().slice(0, 50).map(program => (
                    <div
                      key={program._id}
                      className="p-3 flex items-center justify-between hover:bg-muted/50 cursor-pointer"
                      onClick={() => addProgram(program)}
                    >
                      <div>
                        <div className="font-medium">{program.drug_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {program.entity_id} - {program.dosespot_type}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        Add
                      </Button>
                    </div>
                  ))}
                  {getFilteredAvailablePrograms().length > 50 && (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      Showing 50 of {getFilteredAvailablePrograms().length} programs. Use search to narrow results.
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Required Metrics Tab */}
          <TabsContent value="metrics" className="space-y-4">
            <div>
              <Label htmlFor="required_metrics" className="text-base font-medium">Required Metrics (JSON)</Label>
              <p className="text-sm text-muted-foreground mt-1 mb-2">
                Define metrics that patients must meet for qualification. Each metric can have min, max, avg values, and qualify/disqualify value lists.
              </p>
              <Textarea
                id="required_metrics"
                value={requiredMetricsJson}
                onChange={(e) => handleRequiredMetricsChange(e.target.value)}
                placeholder={DEFAULT_REQUIRED_METRICS}
                className={`font-mono text-sm min-h-[200px] ${jsonErrors.required_metrics || validationErrors.required_metrics ? 'border-red-500' : ''}`}
              />
              {(jsonErrors.required_metrics || validationErrors.required_metrics) && (
                <p className="text-sm text-red-500 mt-1">{jsonErrors.required_metrics || validationErrors.required_metrics}</p>
              )}
              <div className="mt-2 p-3 bg-muted/50 rounded-md">
                <p className="text-xs font-medium mb-1">Example format:</p>
                <pre className="text-xs text-muted-foreground overflow-x-auto">
{`[
  {
    "metric_name": "bmi",
    "min": 25,
    "max": 40,
    "avg": null,
    "qualify_values": [],
    "disqualify_values": []
  }
]`}
                </pre>
              </div>
            </div>
          </TabsContent>

          {/* Marketing Copy Tab */}
          <TabsContent value="marketing" className="space-y-4">
            <div>
              <Label htmlFor="marketing_copy" className="text-base font-medium">Marketing Copy (JSON)</Label>
              <p className="text-sm text-muted-foreground mt-1 mb-2">
                Define marketing content sections. Each section must have a title and can optionally have a description.
              </p>
              <Textarea
                id="marketing_copy"
                value={marketingCopyJson}
                onChange={(e) => handleMarketingCopyChange(e.target.value)}
                placeholder={DEFAULT_MARKETING_COPY}
                className={`font-mono text-sm min-h-[200px] ${jsonErrors.marketing_copy || validationErrors.marketing_copy ? 'border-red-500' : ''}`}
              />
              {(jsonErrors.marketing_copy || validationErrors.marketing_copy) && (
                <p className="text-sm text-red-500 mt-1">{jsonErrors.marketing_copy || validationErrors.marketing_copy}</p>
              )}
              <div className="mt-2 p-3 bg-muted/50 rounded-md">
                <p className="text-xs font-medium mb-1">Example format:</p>
                <pre className="text-xs text-muted-foreground overflow-x-auto">
{`{
  "intake": {
    "title": "Getting Started",
    "description": "Welcome to the program..."
  },
  "benefits": {
    "title": "Program Benefits",
    "description": "Our program offers..."
  }
}`}
                </pre>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save to Drafts'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
